import { dayKey, isWeekendDate, renderDate, toFixedHours } from '@app/utils';
import { isReportLocked, ReportEntity, WorkDayUserEntity } from '@data';
import cx from 'classnames';
import React from 'react';
import Accordion from '@app/components/core/accordion';
import { useTranslation } from 'react-i18next';
import { FULL_WORKDAY_MINUTES } from '@app/constants/app';
import { Button } from '@ui/components/ui/button';
import { Check, Clock, Pencil, Plus, Siren, Trash2, Undo2 } from 'lucide-react';

import { isAbsenceProduct } from './absenceProducts';
import { PendingAbsencesContext } from './pendingAbsences';

const infoBtn = 'focus:outline-none';

interface SprintDayReportProps {
  workDay: WorkDayUserEntity;
  todayDate: Date;
  showAddForm: (workDay: string, productIds?: string[]) => void;
  showEditForm: (report: ReportEntity) => void;
  showDeleteForm: (report: ReportEntity) => void;
  /** When toggled, all report accordions for this day are forced to the matching state. */
  expandAll?: boolean;
  /** The member has called this day done although it carries less than eight hours. */
  isFinished?: boolean;
  /** A full workday across every product, when the day shown is filtered to fewer. Defaults to its own total. */
  isFull?: boolean;
  // Absent for anyone reading somebody else's reporting, which is what hides the control there.
  onSetFinished?: (finished: boolean) => void;
}

const SprintDayReport: React.FC<SprintDayReportProps> = ({
  workDay,
  todayDate,
  showAddForm,
  showEditForm,
  showDeleteForm,
  expandAll = false,
  isFinished = false,
  isFull,
  onSetFinished,
}) => {
  const { t } = useTranslation();
  // Trimmed to what the day still lacks, so hours logged since the last run shrink it straight away.
  const pendingAbsences = React.useContext(PendingAbsencesContext);
  const pending = React.useMemo(() => {
    // Hidden like every other product's entries, and the filtered total cannot size it anyway.
    if (workDay.productsFiltered) return [];
    let left = Math.max(0, FULL_WORKDAY_MINUTES - workDay.totalMinutes);
    return pendingAbsences
      .filter(absence => absence.workday === workDay.date)
      .map(absence => {
        const minutes = Math.min(absence.minutes, left);
        left -= minutes;
        return { ...absence, minutes };
      })
      .filter(absence => absence.minutes > 0);
  }, [pendingAbsences, workDay.date, workDay.totalMinutes, workDay.productsFiltered]);
  const pendingMinutes = pending.reduce((sum, absence) => sum + absence.minutes, 0);
  // Said once for the day rather than on every row, and only when something on it is actually locked.
  const hasLockedReport = workDay.reports.some(isReportLocked);
  const headerEmphasis =
    workDay.reports.length > 0 || workDay.date === dayKey(todayDate)
      ? 'text-neutral-900 dark:text-foreground'
      : 'text-neutral-600 dark:text-muted-foreground';
  // Weekends are tinted to signal they're abnormal reporting days, report rows included — a slate header
  // over plain grey rows reads as two unrelated things. The rows take a lighter wash of the same hue, since
  // matching them exactly turns the day into one flat slab.
  const isWeekend = isWeekendDate(workDay.date);
  // Matched by name, not `billable === false`: internal work is non-billable too and is not a day off.
  const isAbsence = (report: ReportEntity) => isAbsenceProduct(report.involvement?.product?.name);
  const isDayOff =
    workDay.reports.length > 0 ? workDay.reports.every(isAbsence) : pendingMinutes >= FULL_WORKDAY_MINUTES;
  const dayBackground = isWeekend ? 'bg-slate-300 dark:bg-slate-950' : cx('bg-day-head', isDayOff && 'bg-day-off');
  const reportBackground = (report: ReportEntity) =>
    isWeekend ? 'bg-slate-300/70 dark:bg-slate-950/70' : cx('bg-background', isAbsence(report) && 'bg-day-off-rows');
  // The default border all but disappears against the tint, so the weekend divides itself with its own hue.
  const dayBorder = isWeekend ? 'border-slate-400/70 dark:border-slate-800' : 'border-muted-foreground/20';
  const isTodayEmpty = workDay.date === dayKey(todayDate) && workDay.reports.length === 0 && pending.length === 0;
  // A working day short of eight hours is still owed. Weekends are shown for completeness, not asked for.
  const isFullDay = isFull ?? workDay.totalMinutes >= FULL_WORKDAY_MINUTES;
  const isFilledByPending = !isFullDay && workDay.totalMinutes + pendingMinutes >= FULL_WORKDAY_MINUTES;
  const needsReport = !isFullDay && !isFilledByPending && !isWeekend && !isFinished;
  // Nothing to finish on an empty day: that is an unfilled day, not a short one.
  const canFinish = !!onSetFinished && !isWeekend && workDay.reports.length > 0 && !isFullDay;
  return (
    <div className="not-last:mb-5 last:mb-0 overflow-hidden rounded-sm bg-card">
      <div
        className={cx(
          'p-5',
          isTodayEmpty
            ? cx('rounded-t-sm border border-dashed', dayBorder)
            : cx(
                'rounded-t-sm border-x border-t',
                workDay.reports.length > 0 || pending.length === 0 ? 'border-b' : '',
                workDay.reports.length === 0 && pending.length === 0 && 'rounded-b-sm',
                dayBorder,
              ),
          dayBackground,
        )}
      >
        <div className="flex w-full items-center justify-between gap-6">
          <div className="flex min-w-0 flex-1 flex-wrap items-center justify-between gap-2 md:flex-nowrap">
            <p className={cx('flex min-w-0 items-center gap-2 text-base font-semibold', headerEmphasis)}>
              {needsReport && (
                <>
                  <span aria-hidden className="size-2 shrink-0 rounded-full bg-destructive" />
                  {/* The dot is the whole signal otherwise, and colour alone isn't one for everyone. */}
                  <span className="sr-only">{t('dayNotReported')}</span>
                </>
              )}
              {isFilledByPending && !isFinished && (
                <>
                  <span
                    aria-hidden
                    className="size-2 shrink-0 rounded-full border border-dashed border-muted-foreground"
                  />
                  <span className="sr-only">{t('pendingAbsence.dayFilledAtFiling')}</span>
                </>
              )}
              <span className="truncate">{renderDate(workDay.date)}</span>
            </p>
            <p className={cx('shrink-0 font-semibold', headerEmphasis)}>
              {t('hours', { amount: toFixedHours(workDay.totalMinutes / 60) })}
              {pendingMinutes > 0 && (
                <span className="font-normal text-muted-foreground">
                  {' '}
                  {t('pendingAbsence.plusAtFiling', { amount: toFixedHours(pendingMinutes / 60) })}
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center">
            {canFinish && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={isFinished ? t('a11y.reopenDay') : t('a11y.markDayFinished')}
                title={isFinished ? t('a11y.reopenDay') : t('a11y.markDayFinished')}
                className={infoBtn}
                onClick={() => onSetFinished?.(!isFinished)}
              >
                {isFinished ? <Undo2 className="size-4" aria-hidden /> : <Check className="size-4" aria-hidden />}
              </Button>
            )}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={t('a11y.addWorkLogForDay')}
              className={infoBtn}
              onClick={() =>
                showAddForm(
                  workDay.date,
                  // Incident entries book off two clock times, so they never stand in for ordinary work on the product.
                  workDay.reports.filter(report => !report.incidentId).map(report => report.involvement.product.id),
                )
              }
            >
              <Plus className="size-4" aria-hidden />
            </Button>
          </div>
        </div>
      </div>
      {workDay.reports.length > 0 && (
        <>
          {workDay.reports.map((report, index) => {
            const isLast = index === workDay.reports.length - 1 && !hasLockedReport;
            return (
              <Accordion
                // The Accordion syncs its open state from isOpen, so flipping "expand all" animates the
                // height change in place rather than remounting the row.
                key={report.id}
                isOpen={expandAll}
                containerClassName={cx(
                  'box-border border-x px-[14px] py-[15px] focus-within:!shadow-none',
                  !(isLast && pending.length > 0) && 'border-b',
                  dayBorder,
                  reportBackground(report),
                  isLast && pending.length === 0 && 'rounded-b-sm',
                )}
                titleContent={
                  <div className="flex min-w-0 flex-1 flex-wrap items-center justify-between gap-2 text-start md:flex-nowrap">
                    <span className="flex min-w-0 items-center gap-2">
                      <span className="text-foreground">{report.involvement.product.name}</span>
                      {/* Marked on the line rather than filed apart from it: same row, same total, because
                          incident work is ordinary hours on an extraordinary reason. The word rides with
                          the symbol, a colour alone being no signal to everyone. */}
                      {report.incidentId && (
                        <span className="inline-flex shrink-0 items-center gap-1 rounded-md bg-destructive/10 px-1.5 py-0.5 text-[11px] font-medium text-destructive">
                          <Siren className="size-3" aria-hidden />
                          {t('incidents.incidentTag')}
                        </span>
                      )}
                    </span>
                    <span className="shrink-0 text-foreground">
                      {t('hours', { amount: toFixedHours(report.minutes / 60) })}
                    </span>
                  </div>
                }
                headerActions={
                  // Hidden, not disabled, on a locked entry: a disabled control invites a hover and
                  // explains nothing. Per report, not per day — a day can span an open and a closed
                  // sprint product, and locking the row would take away an edit the server allows.
                  isReportLocked(report) ? undefined : (
                    <div className="flex flex-wrap gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={t('a11y.editReport')}
                        onClick={e => {
                          e.stopPropagation();
                          showEditForm(report);
                        }}
                        className={infoBtn}
                      >
                        <Pencil className="size-4" aria-hidden />
                      </Button>

                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={t('a11y.deleteReport')}
                        onClick={e => {
                          e.stopPropagation();
                          showDeleteForm(report);
                        }}
                        className={cx('ml-2', infoBtn)}
                      >
                        <Trash2 className="size-4" aria-hidden />
                      </Button>
                    </div>
                  )
                }
              >
                <div className="flex max-md:flex-col max-md:px-0 max-md:pb-0 sm:justify-between sm:px-14 sm:pb-6">
                  <div className="w-full sm:w-1/2 sm:shrink-0 sm:grow-0 sm:basis-1/2 sm:pl-0">
                    <h3 className="mb-2 font-semibold text-foreground">{t('titles.todayWithoutDate')}</h3>
                    <div className="box-border rounded px-4">
                      <div className="whitespace-pre-wrap text-sm">{report.today}</div>
                    </div>
                  </div>
                </div>
              </Accordion>
            );
          })}
          {hasLockedReport && (
            <p
              className={cx(
                'border-x bg-background px-5 py-3 text-sm text-muted-foreground',
                pending.length === 0 && 'rounded-b-sm border-b',
                dayBorder,
              )}
            >
              {t('overview.sprintClosedNoEdits')}
            </p>
          )}
        </>
      )}
      {pending.map(absence => (
        // Laid out like a report row, but outlined in dashes: it is not on the timesheet until it is filed.
        <div
          key={absence.productName}
          className="dashed-outline box-border bg-background bg-day-off-rows p-[15px] last:rounded-b-sm"
        >
          <div className="flex w-full items-center">
            <div className="flex min-w-0 flex-1 items-center px-6 py-5">
              <span aria-hidden className="mr-2 flex size-5 shrink-0 items-center justify-center text-muted-foreground">
                <Clock className="size-4" />
              </span>
              <div className="flex min-w-0 flex-1 items-center justify-between gap-2">
                <span className="flex min-w-0 flex-col">
                  <span className="text-foreground">{absence.productName}</span>
                  <span className="text-xs text-muted-foreground">{t('pendingAbsence.hint')}</span>
                </span>
                <span className="shrink-0 text-muted-foreground">
                  {t('hours', { amount: toFixedHours(absence.minutes / 60) })}
                </span>
              </div>
            </div>
            <span aria-hidden className="flex shrink-0 gap-1 pr-6">
              <span className="size-8" />
              <span className="ml-2 size-8" />
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};

export default SprintDayReport;
